/**
 * Shared time constants of `apps/web`.
 *
 * PATTERN TO FOLLOW: any cache, debounce or timeout duration used
 * in more than one place (or representing a product decision, not a local
 * detail) lives here, not as a loose `const` inside a component.
 *
 * Reason: these values were redeclared in ~12 files with two different
 * spellings of the same number (`5 * 60 * 1000` and `300_000`), so that
 * changing the cache policy forced touching all those places and risked
 * leaving divergent values. Import from here instead of redefining.
 */

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;

/** Milliseconds in a minute. Base for deriving the rest of the durations. */
const MINUTE_MS = MS_PER_SECOND * SECONDS_PER_MINUTE;

/** Milliseconds in an hour. Used by the foreground reminders window. */

/** Minutes a cached datum is considered fresh (offline cache and session). */
const STALE_MINUTES = 5;

/**
 * Default `staleTime` of the offline-first queries (PowerSync + TanStack).
 *
 * Data is synced locally via PowerSync, so a generous `staleTime`
 * (5 min) avoids redundant refetches/re-renders with no real risk of
 * showing stale data. It is also the global default of the `queryClient`.
 */
export const OFFLINE_CACHE_STALE_TIME_MS = STALE_MINUTES * MINUTE_MS;

/**
 * `staleTime` of the session/authentication queries.
 *
 * It is kept as its own constant (even though today it matches the offline
 * cache value) because it is an independent decision: it must be aligned
 * between `useSession`, the `_app` route guard and the `session-bridge`.
 */
export const SESSION_STALE_TIME_MS = STALE_MINUTES * MINUTE_MS;

/**
 * Time cap for the session check in the route guards.
 *
 * P23/CC-60 · I1-bis. SINGLE source for BOTH guards (`_app` and `_auth`):
 * having it as a local literal inside `_app/route.tsx` was exactly what
 * left `_auth` without the defense `_app` did have — and that is why `/` painted with
 * `get-session` stalled and `/login` stayed BLANK (measured: empty `innerText`
 * at 3/6/10/15/20 s, also on the production bundle).
 *
 * Once the cap passes the screen PAINTS; the late response stays alive and
 * syncs machine + cache when it arrives, and the `BootManager` redirects afterward
 * if there turned out to be a session. A flicker is paid, never a blank screen.
 */
export const SESSION_CHECK_TIMEOUT_MS = 1500;

/**
 * Debounce for text inputs that persist while typing (title and detail
 * notes, subtasks). Balances typing fluency against the number of mutations.
 */

/** Seconds before revoking a download's object URL (see constant). */
const OBJECT_URL_REVOKE_SECONDS = 10;

/**
 * Margin before revoking a `blob:` object URL after triggering a download.
 *
 * It must be comfortably larger than what the browser takes to consume the href
 * of the `<a download>`; revoking earlier would abort the download.
 */
export const OBJECT_URL_REVOKE_DELAY_MS = OBJECT_URL_REVOKE_SECONDS * MS_PER_SECOND;
